import React from "react";

import {
    LifecycleStatusBadge,
    type LifecycleStatusTone
} from "../console/badges";

/**
 * Policy rule status as represented in the badge.
 * Matches `PolicyRuleStatus` in the issuer-app types.
 */
export type PolicyStatus = "active" | "draft" | "paused" | "expired";

interface PolicyStatusBadgeProps {
    status: PolicyStatus;
    className?: string;
}

const STATUS_TONE: Record<PolicyStatus, LifecycleStatusTone> = {
    active: "success",
    draft: "pending",
    paused: "disabled",
    expired: "disabled"
};

const LABELS: Record<PolicyStatus, string> = {
    active: "Active",
    draft: "Draft",
    paused: "Paused",
    expired: "Expired"
};

/**
 * Status pill badge for policy rules.
 * Shows a colored dot + status label, styled to match the prototype's StatusPill.
 * Color tokens map to Solomon brand:
 *   active  → success-base (green)
 *   draft   → warning-base (amber/gold)
 *   paused  → muted charcoal
 *   expired → muted charcoal
 */
export function PolicyStatusBadge({
    status,
    className
}: PolicyStatusBadgeProps): React.ReactElement {
    return (
        <LifecycleStatusBadge state={STATUS_TONE[status]} className={className}>
            {LABELS[status]}
        </LifecycleStatusBadge>
    );
}
